import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { WebhookEvent } from '../types';
import { RefreshCw, Webhook, Lock, AlertTriangle, Save, Plus, Send, RotateCcw, Download } from 'lucide-react';

const LIMIT = 20;

const STATUS_BADGE: Record<string, string> = {
  DELIVERED: 'delivered',
  FAILED: 'failed',
  PENDING: 'pending',
  RETRYING: 'in-transit',
};

const EVENT_TYPES = [
  'shipment.created', 'shipment.confirmed', 'shipment.dispatched',
  'shipment.in_transit', 'shipment.delivered', 'shipment.returned',
  'shipment.cancelled', 'payment.created', 'payment.completed', 'payment.updated', 'payment.failed',
];

type WebhookEndpointRecord = {
  id: string;
  name: string;
  url: string;
  status: 'ACTIVE' | 'DISABLED';
  eventSubscriptions?: string[] | null;
  createdAt: string;
};

type ExportJob = {
  id: string;
  resource: 'shipments' | 'payments';
  format: 'csv' | 'ndjson';
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  filePath?: string | null;
  lastError?: string | null;
  createdAt: string;
};

type ExportSchedule = {
  id: string;
  resource: 'shipments' | 'payments';
  format: 'csv' | 'ndjson';
  enabled: boolean;
  frequencyMinutes: number;
  nextRunAt: string;
};

export const Webhooks: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCompanyData, setSelectedCompanyData] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [endpoints, setEndpoints] = useState<WebhookEndpointRecord[]>([]);
  const [creatingEndpoint, setCreatingEndpoint] = useState(false);
  const [newEndpointName, setNewEndpointName] = useState('');
  const [newEndpointUrl, setNewEndpointUrl] = useState('');
  const [newEndpointSubscriptions, setNewEndpointSubscriptions] = useState<string[]>([]);
  const [newEndpointSecret, setNewEndpointSecret] = useState<string | null>(null);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [schedules, setSchedules] = useState<ExportSchedule[]>([]);
  const [creatingJob, setCreatingJob] = useState(false);
  const [jobResource, setJobResource] = useState<'shipments' | 'payments'>('shipments');
  const [jobFormat, setJobFormat] = useState<'csv' | 'ndjson'>('csv');
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [scheduleResource, setScheduleResource] = useState<'shipments' | 'payments'>('shipments');
  const [scheduleFormat, setScheduleFormat] = useState<'csv' | 'ndjson'>('csv');
  const [scheduleFrequency, setScheduleFrequency] = useState(1440);

  // Webhook URL config state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlSaved, setUrlSaved] = useState(false);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const webhooksEnabled = isSuperAdmin || selectedCompanyData?.allowWebhooks;
  const canConfigureUrl = isSuperAdmin || user?.role === 'COMPANY_ADMIN';

  useEffect(() => {
    if (isSuperAdmin) {
      apiClient.listCompanies(100, 0).then(res => {
        const data = res.data.data;
        const list: any[] = data?.companies ?? data?.rows ?? (Array.isArray(data) ? data : []);
        setCompanies(list);
        if (list.length > 0) {
          setSelectedCompany(list[0].id);
          setSelectedCompanyData(list[0]);
          setWebhookUrl(list[0].webhookUrl || '');
        }
      }).catch(() => {});
    } else if (user?.companyId) {
      setSelectedCompany(user.companyId);
      apiClient.getCompany(user.companyId).then(res => {
        const c = res.data.data;
        setSelectedCompanyData(c);
        setWebhookUrl(c?.webhookUrl || '');
      }).catch(() => {});
    }
  }, [isSuperAdmin, user]);

  const handleCompanyChange = (id: string) => {
    setSelectedCompany(id);
    setPage(0);
    const found = companies.find((c: any) => c.id === id);
    setSelectedCompanyData(found || null);
    setWebhookUrl(found?.webhookUrl || '');
  };

  const load = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const res = await apiClient.listWebhookEvents(selectedCompany, LIMIT, page * LIMIT, {
        status: filterStatus || undefined,
        eventType: filterType || undefined,
      });
      const data = res.data.data;
      setEvents(data?.events ?? data?.rows ?? (Array.isArray(data) ? data : []));
      setTotal(data?.total ?? data?.count ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [selectedCompany, page, filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const loadEndpointsAndExports = useCallback(async () => {
    if (!selectedCompany || !canConfigureUrl) return;
    try {
      const [eRes, jRes, sRes] = await Promise.all([
        apiClient.listWebhookEndpoints(selectedCompany),
        apiClient.listExportJobs(selectedCompany, 10, 0),
        apiClient.listExportSchedules(selectedCompany),
      ]);
      setEndpoints(eRes.data.data || []);
      setJobs(jRes.data.data?.jobs || []);
      setSchedules(sRes.data.data || []);
    } catch {
      // silent
    }
  }, [selectedCompany, canConfigureUrl]);

  useEffect(() => {
    loadEndpointsAndExports();
  }, [loadEndpointsAndExports]);

  const handleRetry = async (id: string) => {
    setRetrying(id);
    try { await apiClient.retryWebhookEvent(id); await load(); }
    catch { /* silent */ }
    finally { setRetrying(null); }
  };

  const handleEnableWebhooks = async () => {
    try {
      await apiClient.toggleWebhooks(selectedCompany, true);
      setSelectedCompanyData((p: any) => ({ ...p, allowWebhooks: true }));
    } catch (err: any) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handleSaveUrl = async () => {
    setSavingUrl(true);
    try {
      await apiClient.updateWebhookUrl(selectedCompany, webhookUrl || null);
      setSelectedCompanyData((p: any) => ({ ...p, webhookUrl }));
      setUrlSaved(true);
      setTimeout(() => setUrlSaved(false), 2500);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save URL');
    } finally {
      setSavingUrl(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const toggleSubscription = (evt: string) => {
    setNewEndpointSubscriptions(prev => prev.includes(evt) ? prev.filter(x => x !== evt) : [...prev, evt]);
  };

  const handleCreateEndpoint = async () => {
    if (!selectedCompany || !newEndpointName || !newEndpointUrl) return;
    setCreatingEndpoint(true);
    setNewEndpointSecret(null);
    try {
      const res = await apiClient.createWebhookEndpoint(selectedCompany, {
        name: newEndpointName,
        url: newEndpointUrl,
        eventSubscriptions: newEndpointSubscriptions.length ? newEndpointSubscriptions : null,
      });
      setNewEndpointSecret(res.data?.data?.secret || null);
      setNewEndpointName('');
      setNewEndpointUrl('');
      setNewEndpointSubscriptions([]);
      await loadEndpointsAndExports();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create endpoint');
    } finally {
      setCreatingEndpoint(false);
    }
  };

  const handleTestEndpoint = async (id: string) => {
    try {
      await apiClient.testWebhookEndpoint(id);
      await load();
      await loadEndpointsAndExports();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to test endpoint');
    }
  };

  const handleRotateEndpointSecret = async (id: string) => {
    try {
      const res = await apiClient.rotateWebhookEndpointSecret(id);
      const secret = res.data?.data?.secret;
      if (secret) {
        setNewEndpointSecret(secret);
      } else {
        alert('Secret rotated successfully');
      }
      await loadEndpointsAndExports();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to rotate secret');
    }
  };

  const handleCreateExportJob = async () => {
    if (!selectedCompany) return;
    setCreatingJob(true);
    try {
      await apiClient.createExportJob(selectedCompany, { resource: jobResource, format: jobFormat });
      await loadEndpointsAndExports();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create export job');
    } finally {
      setCreatingJob(false);
    }
  };

  const handleDownloadJob = async (jobId: string) => {
    try {
      const res = await apiClient.downloadExportJob(jobId);
      const blob = new Blob([res.data]);
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${jobId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to download export');
    }
  };

  const handleCreateSchedule = async () => {
    if (!selectedCompany) return;
    setCreatingSchedule(true);
    try {
      await apiClient.createExportSchedule(selectedCompany, {
        resource: scheduleResource,
        format: scheduleFormat,
        frequencyMinutes: scheduleFrequency,
      });
      await loadEndpointsAndExports();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create schedule');
    } finally {
      setCreatingSchedule(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Webhook Events</h1>
        <button className="btn btn-ghost" onClick={load}><RefreshCw size={15} /> Refresh</button>
      </div>

      {isSuperAdmin && companies.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <select value={selectedCompany} onChange={e => handleCompanyChange(e.target.value)} style={{ width: 280 }}>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.allowWebhooks ? '' : '(Webhooks disabled)'}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Webhooks disabled — company admin view */}
      {selectedCompany && !webhooksEnabled && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <AlertTriangle size={22} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Webhooks are not enabled for your company</div>
            <div style={{ fontSize: 13, color: '#78350f' }}>
              Contact your Super Admin to enable webhook delivery.
            </div>
          </div>
        </div>
      )}

      {/* Super admin: enable webhooks inline */}
      {isSuperAdmin && selectedCompanyData && !selectedCompanyData.allowWebhooks && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '14px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Lock size={18} color="#0369a1" />
            <span style={{ fontSize: 14, color: '#0c4a6e' }}>
              Webhooks are <strong>disabled</strong> for <strong>{selectedCompanyData.name}</strong>
            </span>
          </div>
          <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={handleEnableWebhooks}>
            Enable Webhooks
          </button>
        </div>
      )}

      {/* Webhook URL configuration */}
      {webhooksEnabled && canConfigureUrl && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Webhook Endpoint URL</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
            All shipment and payment events will be POSTed to this URL with an{' '}
            <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>X-Webhook-Signature</code>{' '}
            header for verification. Leave blank to disable delivery.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              type="url"
              value={webhookUrl}
              onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://your-system.com/webhooks/logistics"
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap', fontSize: 13 }}
              onClick={handleSaveUrl}
              disabled={savingUrl}
            >
              <Save size={14} /> {urlSaved ? 'Saved!' : savingUrl ? 'Saving...' : 'Save URL'}
            </button>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
            Verify signatures with:{' '}
            <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 3 }}>
              HMAC-SHA256(secret, &quot;{'{timestamp}.{body}'}&quot;) === X-Webhook-Signature
            </code>
          </div>
        </div>
      )}

      {/* Multi-endpoint webhook management */}
      {webhooksEnabled && canConfigureUrl && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Webhook Endpoints (Multi-endpoint)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
            <input value={newEndpointName} onChange={e => setNewEndpointName(e.target.value)} placeholder="Endpoint name" />
            <input value={newEndpointUrl} onChange={e => setNewEndpointUrl(e.target.value)} placeholder="https://example.com/webhooks/logistics" />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {EVENT_TYPES.map(evt => (
              <label key={evt} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={newEndpointSubscriptions.includes(evt)} onChange={() => toggleSubscription(evt)} />
                {evt}
              </label>
            ))}
          </div>
          <button className="btn btn-primary" disabled={creatingEndpoint} onClick={handleCreateEndpoint}>
            <Plus size={14} /> {creatingEndpoint ? 'Creating...' : 'Create Endpoint'}
          </button>
          {newEndpointSecret && (
            <div style={{ marginTop: 10, padding: 10, background: '#fef9c3', borderRadius: 8, fontSize: 12 }}>
              Save endpoint secret now (shown once): <code>{newEndpointSecret}</code>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            {endpoints.length === 0 ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>No endpoints configured yet.</div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Name</th><th>URL</th><th>Status</th><th>Subscriptions</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {endpoints.map(ep => (
                      <tr key={ep.id}>
                        <td>{ep.name}</td>
                        <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ep.url}</td>
                        <td>{ep.status}</td>
                        <td style={{ fontSize: 12 }}>{ep.eventSubscriptions?.length ? ep.eventSubscriptions.join(', ') : 'ALL'}</td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost" onClick={() => handleTestEndpoint(ep.id)}><Send size={12} /> Test</button>
                          <button className="btn btn-ghost" onClick={() => handleRotateEndpointSecret(ep.id)}><RotateCcw size={12} /> Rotate secret</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export jobs + schedules */}
      {canConfigureUrl && selectedCompany && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Exports & BI Connector</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <select value={jobResource} onChange={e => setJobResource(e.target.value as any)} style={{ width: 140 }}>
              <option value="shipments">shipments</option>
              <option value="payments">payments</option>
            </select>
            <select value={jobFormat} onChange={e => setJobFormat(e.target.value as any)} style={{ width: 120 }}>
              <option value="csv">csv</option>
              <option value="ndjson">ndjson</option>
            </select>
            <button className="btn btn-primary" disabled={creatingJob} onClick={handleCreateExportJob}>
              {creatingJob ? 'Creating...' : 'Create Export Job'}
            </button>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Recent jobs</div>
            {jobs.length === 0 ? <div style={{ fontSize: 12, color: '#64748b' }}>No jobs yet.</div> : (
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Resource</th><th>Format</th><th>Status</th><th>Created</th><th></th></tr></thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr key={j.id}>
                        <td>{j.resource}</td><td>{j.format}</td><td>{j.status}</td>
                        <td style={{ fontSize: 12 }}>{new Date(j.createdAt).toLocaleString()}</td>
                        <td>{j.status === 'COMPLETED' && <button className="btn btn-ghost" onClick={() => handleDownloadJob(j.id)}><Download size={12} /> Download</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Create schedule</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={scheduleResource} onChange={e => setScheduleResource(e.target.value as any)} style={{ width: 140 }}>
                <option value="shipments">shipments</option>
                <option value="payments">payments</option>
              </select>
              <select value={scheduleFormat} onChange={e => setScheduleFormat(e.target.value as any)} style={{ width: 120 }}>
                <option value="csv">csv</option>
                <option value="ndjson">ndjson</option>
              </select>
              <input type="number" value={scheduleFrequency} min={15} onChange={e => setScheduleFrequency(Number(e.target.value))} style={{ width: 120 }} />
              <button className="btn btn-primary" disabled={creatingSchedule} onClick={handleCreateSchedule}>
                {creatingSchedule ? 'Saving...' : 'Save schedule'}
              </button>
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
              Existing schedules: {schedules.length}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {webhooksEnabled && events.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }} style={{ width: 160 }}>
            <option value="">All statuses</option>
            {['PENDING', 'DELIVERED', 'FAILED', 'RETRYING'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0); }} style={{ width: 200 }}>
            <option value="">All event types</option>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-spinner">Loading events...</div>
        ) : webhooksEnabled ? (
          events.length === 0 ? (
            <div className="empty-state">
              <Webhook size={32} color="#cbd5e1" style={{ marginBottom: 12 }} />
              <h3>No webhook events yet</h3>
              <p style={{ fontSize: 13 }}>Events appear here when shipments or payments change status.</p>
            </div>
          ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Event Type</th>
                    <th>Delivery</th>
                    <th>Attempts</th>
                    <th>HTTP</th>
                    <th>Webhook URL</th>
                    <th>Last Error</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(e => (
                    <tr key={e.id}>
                      <td><code style={{ fontSize: 12 }}>{e.eventType}</code></td>
                      <td>
                        <span className={`badge badge-${STATUS_BADGE[e.deliveryStatus] || 'pending'}`}>
                          {e.deliveryStatus}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>{e.deliveryAttempts}</td>
                      <td style={{ textAlign: 'center', fontSize: 12 }}>
                        {e.responseCode ? (
                          <span style={{ color: e.responseCode >= 200 && e.responseCode < 300 ? '#16a34a' : '#dc2626' }}>
                            {e.responseCode}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {e.webhookUrl || '—'}
                      </td>
                      <td style={{ maxWidth: 200, fontSize: 12, color: '#ef4444' }}>
                        {e.lastError ? e.lastError.slice(0, 60) : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {new Date(e.createdAt).toLocaleString()}
                      </td>
                      <td>
                        {['FAILED', 'PENDING'].includes(e.deliveryStatus) && (
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            onClick={() => handleRetry(e.id)}
                            disabled={retrying === e.id}
                          >
                            <RefreshCw size={12} /> {retrying === e.id ? '...' : 'Retry'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
          )
        ) : (
          <div className="empty-state">
            <Lock size={32} color="#cbd5e1" style={{ marginBottom: 12 }} />
            <h3>Webhooks Disabled</h3>
            <p style={{ fontSize: 13 }}>Contact your Super Admin to enable webhook delivery.</p>
          </div>
        )}
      </div>
    </>
  );
};
